<template>
  <section class="flex flex-col items-center justify-center overflow-hidden px-0 py-0 lg:px-16 lg:py-16 relative w-full bg-white">
    <div class="mx-auto w-full max-w-[1200px]">
      <div
        class="flex flex-col gap-8 px-4 py-12 lg:gap-12 lg:rounded-3xl lg:p-16"
        :style="{ backgroundColor: panelColor }"
      >
        <div class="flex w-full flex-col items-center gap-12">
          <div class="flex flex-col items-center gap-3 text-center text-neutral-900">
            <h2 class="text-[32px] font-semibold leading-10 tracking-[-0.64px] lg:text-[36px] lg:leading-[44px] lg:tracking-[-0.02em]">
              {{ headingText }}
            </h2>
            <div
              v-if="ratingScore || ratingSubtitle"
              class="flex items-center gap-2"
            >
              <div class="flex h-6 items-start" aria-hidden="true">
                <span
                  v-for="star in 5"
                  :key="star"
                  class="-mr-0.5 text-2xl leading-6 text-amber-400"
                >★</span>
              </div>
              <p class="text-base leading-6">
                <span v-if="ratingScore" class="font-semibold text-neutral-900">{{ ratingScore }}</span>
                <span v-if="ratingSubtitle" class="text-neutral-500"> {{ ratingSubtitle }}</span>
              </p>
            </div>
          </div>

          <div
            ref="viewportRef"
            class="testimonials-carousel-viewport w-full cursor-grab overflow-hidden touch-pan-y"
            :class="viewportClass"
            v-bind="swipeHandlers"
          >
            <div
              data-carousel-track
              class="flex gap-8"
              :class="[trackClass, trackTransitionClass]"
              :style="trackStyle"
            >
              <article
                v-for="(testimonial, idx) in testimonials"
                :key="idx"
                class="flex min-h-[180px] lg:min-h-[220px] w-[var(--card-width)] shrink-0 flex-col gap-5 rounded-md border border-gray-100 bg-white p-6 shadow-sm"
              >
                <p class="line-clamp-[8] lg:line-clamp-5 text-lg font-semibold leading-6 tracking-[-0.36px] text-neutral-900 lg:text-xl lg:leading-7 lg:tracking-[-0.02em]">
                  {{ testimonial.body }}
                </p>
                <p class="mt-auto text-lg leading-7 text-neutral-600">
                  {{ testimonial.author }}
                </p>
              </article>
            </div>
          </div>
        </div>

        <template v-if="testimonials.length > pageSize">
          <!-- Mobile: dots left, white ghost arrows right (Figma KLNP mobile) -->
          <div class="flex lg:hidden h-11 w-full items-center justify-between">
            <CarouselDots
              :count="dotCount"
              :active-index="slideIndex"
              :dot-size="8"
              :gap="12"
            >
              <template #default="{ index, active }">
                <button
                  type="button"
                  class="size-2 shrink-0 cursor-pointer rounded-full border-0 p-0 transition-opacity duration-300 ease-in-out"
                  :class="active ? 'opacity-100' : 'opacity-30'"
                  :style="{ backgroundColor: '#262626' }"
                  :aria-label="t('carousel.testimonials_goto', { n: index + 1 })"
                  @click="goToSlide(index)"
                />
              </template>
            </CarouselDots>

            <div class="flex items-center gap-3">
              <CarouselArrowButton
                direction="prev"
                :aria-label="t('carousel.testimonials_prev')"
                :disabled="!canGoPrev"
                @click="prev"
              />
              <CarouselArrowButton
                direction="next"
                :aria-label="t('carousel.testimonials_next')"
                :disabled="!canGoNext"
                @click="next"
              />
            </div>
          </div>

          <!-- Desktop: centered dots, theme-colored arrows -->
          <div class="relative hidden lg:block h-11 w-full">
            <CarouselDots
              class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
              :count="dotCount"
              :active-index="slideIndex"
              :dot-size="6"
              :gap="8"
            >
              <template #default="{ active }">
                <span
                  class="size-1.5 shrink-0 rounded-full transition-all duration-300 ease-in-out"
                  :class="active ? 'opacity-100 scale-125' : 'opacity-40 scale-100'"
                  :style="{ backgroundColor: '#262626' }"
                />
              </template>
            </CarouselDots>

            <CarouselArrowButton
              direction="next"
              class="absolute right-0 top-1/2 -translate-y-1/2"
              :aria-label="t('carousel.testimonials_next')"
              :disabled="!canGoNext"
              @click="next"
            />
            <CarouselArrowButton
              direction="prev"
              class="absolute right-14 top-1/2 -translate-y-1/2"
              :aria-label="t('carousel.testimonials_prev')"
              :disabled="!canGoPrev"
              @click="prev"
            />
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { themeCardColorFor } from '@/utils/siteThemeColors';
import { useTranslations } from '@/utils/translations.js';
import CarouselArrowButton from '@/Shared/CarouselArrowButton.vue';
import CarouselDots from '@/Shared/CarouselDots.vue';
import { useCarouselSwipe } from '@/utils/useCarouselSwipe.js';
import { carouselTrackTransform } from '@/utils/carouselTrack.js';

const { t } = useTranslations();

const props = defineProps({
  headingText:    { type: String, default: 'Opinie naszych pacjentów' },
  ratingScore:    { type: String, default: '4,8' },
  ratingSubtitle: { type: String, default: '(417 opinii z Google)' },
  body1: { type: String, default: '' }, author1: { type: String, default: '' },
  body2: { type: String, default: '' }, author2: { type: String, default: '' },
  body3: { type: String, default: '' }, author3: { type: String, default: '' },
  body4: { type: String, default: '' }, author4: { type: String, default: '' },
  body5: { type: String, default: '' }, author5: { type: String, default: '' },
  body6: { type: String, default: '' }, author6: { type: String, default: '' },
  body7: { type: String, default: '' }, author7: { type: String, default: '' },
  body8: { type: String, default: '' }, author8: { type: String, default: '' },
  body9: { type: String, default: '' }, author9: { type: String, default: '' },
  // eslint-disable-next-line vue/prop-name-casing
  site_context: { type: String, default: null },
});

const MOBILE_BREAKPOINT = 1024;

const panelColor = computed(() => themeCardColorFor(props.site_context));

const testimonials = computed(() => {
  const out = [];
  for (let i = 1; i <= 9; i += 1) {
    const body = props[`body${i}`];
    if (!body) continue;
    out.push({ body, author: props[`author${i}`] });
  }
  return out;
});

const slideIndex = ref(0);
const isMobileViewport = ref(false);
let mobileMediaQuery = null;
let syncMobileViewport = null;

const pageSize = computed(() => (isMobileViewport.value ? 1 : 3));

const maxSlideIndex = computed(() => (
  Math.max(0, testimonials.value.length - pageSize.value)
));

const dotCount = computed(() => maxSlideIndex.value + 1);

const canGoPrev = computed(() => slideIndex.value > 0);
const canGoNext = computed(() => slideIndex.value < maxSlideIndex.value);

const {
  viewportRef,
  viewportClass,
  trackClass,
  trackTransitionClass,
  swipeHandlers,
} = useCarouselSwipe({
  onNext: () => next(),
  onPrev: () => prev(),
  enabled: () => testimonials.value.length > pageSize.value,
  canGoNext,
  canGoPrev,
});

const trackStyle = computed(() => carouselTrackTransform(
  `-1 * ${slideIndex.value} * (var(--card-width) + var(--card-gap))`,
));

watch(maxSlideIndex, (max) => {
  if (slideIndex.value > max) slideIndex.value = max;
});

onMounted(() => {
  if (typeof window === 'undefined') return;

  mobileMediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
  syncMobileViewport = () => {
    isMobileViewport.value = mobileMediaQuery.matches;
  };
  syncMobileViewport();
  mobileMediaQuery.addEventListener('change', syncMobileViewport);
});

onBeforeUnmount(() => {
  if (mobileMediaQuery && syncMobileViewport) {
    mobileMediaQuery.removeEventListener('change', syncMobileViewport);
  }
});

function next() {
  if (!canGoNext.value) return;
  slideIndex.value += 1;
}

function prev() {
  if (!canGoPrev.value) return;
  slideIndex.value -= 1;
}

function goToSlide(index) {
  slideIndex.value = Math.min(Math.max(0, index), maxSlideIndex.value);
}
</script>

<style scoped>
.testimonials-carousel-viewport {
  --card-width: 100%;
  --card-gap: 2rem;
}

@media (min-width: 1024px) {
  .testimonials-carousel-viewport {
    --card-width: calc((100% - 2 * var(--card-gap)) / 3);
  }
}
</style>
